import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import api, { formatApiError, BACKEND } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionCard, StatusBadge, GradeBadge } from "@/components/common/Primitives";
import { StageBadge } from "@/components/common/StageBadge";
import { ProgressBadge, StageChip, CtaIcon } from "@/components/common/AuditBadges";
import { getAuditDisplayState } from "@/lib/auditStatus";
import { ArrowLeft, Camera, Save, Send, AlertTriangle, CheckCircle2, XCircle, FileText, MessageSquare, Trash2, Plus, PlayCircle, Lock, Info } from "lucide-react";
import { useAuth, hasRole } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import ImageLightbox from "@/components/common/ImageLightbox";

export default function AuditDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [audit, setAudit] = useState(null);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [findingDlg, setFindingDlg] = useState(false);
  const [findingForm, setFindingForm] = useState({ severity: "Medium", description: "", recommendation: "" });
  const [reviewDlg, setReviewDlg] = useState(null); // 'approve' | 'reject' | 'command'
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewCommand, setReviewCommand] = useState("");
  const [uploadForQuestion, setUploadForQuestion] = useState(null);
  // Image lightbox state — carries a list of {url, filename} and the current index.
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const openLightbox = (atts, index) => setLightbox({
    open: true,
    images: atts.map((a) => ({
      url: a.url?.startsWith("http") ? a.url : `${BACKEND}${a.url}`,
      filename: a.filename,
      id: a.id,
    })),
    index,
  });

  // Option-A draft flow: while status='planned', score clicks live in local state only.
  // Save Draft flushes them to the server and flips the audit into in_progress.
  const [pendingAnswers, setPendingAnswers] = useState({}); // question_id -> {score, remark}
  const [savingDraft, setSavingDraft] = useState(false);

  const load = async () => {
    try {
      const [aRes, cRes, qRes] = await Promise.all([
        api.get(`/audits/${id}`), api.get("/categories"), api.get("/questions"),
      ]);
      setAudit(aRes.data); setCategories(cRes.data); setQuestions(qRes.data);
      if (!activeCat && cRes.data[0]) setActiveCat(cRes.data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // ─── Leave-page guard while there are unsaved local scores ───
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    Object.keys(pendingAnswers).length > 0 && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const ok = window.confirm("You have unsaved answers. Leave without saving?");
    if (ok) { setPendingAnswers({}); blocker.proceed(); }
    else { blocker.reset(); }
  }, [blocker]);
  useEffect(() => {
    const handler = (e) => {
      if (Object.keys(pendingAnswers).length === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pendingAnswers]);

  const answersByQ = useMemo(() => {
    const m = {};
    (audit?.answers || []).forEach((a) => { m[a.question_id] = a; });
    return m;
  }, [audit]);

  // Effective answer per question = pending (in-flight local edits) overrides saved.
  const effectiveByQ = useMemo(() => {
    const m = { ...answersByQ };
    Object.entries(pendingAnswers).forEach(([qid, val]) => {
      m[qid] = { ...(m[qid] || { question_id: qid }), ...val, _pending: true };
    });
    return m;
  }, [answersByQ, pendingAnswers]);

  const hasUnsavedChanges = Object.keys(pendingAnswers).length > 0;
  const attByQ = useMemo(() => {
    const m = {};
    (audit?.attachments || []).forEach((a) => {
      if (!a.question_id) return;
      m[a.question_id] = m[a.question_id] || [];
      m[a.question_id].push(a);
    });
    return m;
  }, [audit]);

  const catQuestions = questions.filter((q) => q.category_id === activeCat);
  const catAnswered = catQuestions.filter((q) => answersByQ[q.id] != null).length;

  // Edit rules (align with backend _check_edit_allowed):
  //   Pre-Audit stage: planned / in_progress / rejected → editable
  //   Pre-Audit approved → NOT editable (auditor must click Start Post-Audit)
  //   Post-Audit stage: planned / in_progress / rejected → editable
  //   Submitted / Closed (or Post-Audit approved which aliases to closed) → NOT editable
  const isAssigned = hasRole(user, "auditor") && (audit?.auditors || []).includes(user?.id);
  const _phase = audit?.phase || "pre_audit";
  const _status = audit?.status || "planned";
  const isPreApprovedGate = _phase === "pre_audit" && _status === "approved";
  const canAnswer = hasRole(user, "auditor") && isAssigned && !isPreApprovedGate &&
    ["planned", "in_progress", "rejected"].includes(_status);
  // Submit only when at least in_progress or rejected — never on planned or the pre-approved gate.
  const canSubmit = hasRole(user, "auditor") && isAssigned && !isPreApprovedGate &&
    ["in_progress", "rejected"].includes(_status);
  const canReview = hasRole(user, "manager") && _status === "submitted";

  // Can this auditor click "Start Audit"?
  const canStart = _status === "planned" && isAssigned;

  // Photo evidence is OPTIONAL — kept upload capability but no submit block.

  // Questions with score 1 or 2 but missing remark (mandatory justification for low scores)
  const remarkMissing = useMemo(() => {
    const missing = [];
    (audit?.answers || []).forEach((a) => {
      const s = Number(a.score);
      if ((s === 1 || s === 2) && !(a.remark || "").trim()) {
        missing.push(a.question_id);
      }
    });
    return missing;
  }, [audit]);

  async function startAudit() {
    try {
      await api.post(`/audits/${id}/start`);
      toast.success("Audit started. Questions are now active.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function saveAnswer(q, score, remark) {
    // While the audit is still 'planned', all edits are held in local pending state
    // and only committed to the server via Save Draft. This is the Option-A rule:
    // status only flips to in_progress on an explicit Save Draft.
    if (audit?.status === "planned") {
      setPendingAnswers((prev) => ({
        ...prev,
        [q.id]: {
          question_id: q.id,
          score,
          remark: remark ?? prev[q.id]?.remark ?? null,
        },
      }));
      return;
    }

    // In-progress / rejected: standard autosave path
    try {
      await api.post(`/audits/${id}/answers`, { audit_id: id, question_id: q.id, score, remark: remark || null });
      if ((score === 1 || score === 2) && !(remark || "").trim()) {
        toast.warning(`Score ${score} requires a remark explaining the finding.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function saveDraft() {
    const items = Object.values(pendingAnswers);
    if (items.length === 0) {
      toast.warning("Add at least one score before saving a draft.");
      return;
    }
    setSavingDraft(true);
    try {
      const { data } = await api.patch(`/audits/${id}/draft`, {
        answers: items.map((a) => ({ question_id: a.question_id, score: a.score, remark: a.remark || null })),
      });
      setPendingAnswers({});
      toast.success(
        data?.transitioned_to_in_progress
          ? `Draft saved · ${items.length} answer(s) · Status → In Progress`
          : `Draft saved · ${items.length} answer(s)`
      );
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingDraft(false); }
  }

  async function submitAudit() {
    if (remarkMissing.length > 0) {
      return toast.error(`Cannot submit — ${remarkMissing.length} question(s) with score 1 or 2 are missing a remark.`);
    }
    if (!window.confirm("Submit this audit for manager review? This will lock the answers.")) return;
    try {
      await api.post(`/audits/${id}/submit`);
      toast.success("Audit submitted for review");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function addFinding() {
    try {
      await api.post(`/audits/${id}/findings`, { audit_id: id, ...findingForm });
      toast.success("Finding added");
      setFindingDlg(false);
      setFindingForm({ severity: "Medium", description: "", recommendation: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function deleteFinding(fid) {
    if (!window.confirm("Delete finding?")) return;
    try { await api.delete(`/audits/${id}/findings/${fid}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (uploadForQuestion) fd.append("question_id", uploadForQuestion);
    try {
      await api.post(`/audits/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      setUploadForQuestion(null);
      load();
    } catch (er) { toast.error(formatApiError(er)); }
    e.target.value = "";
  }

  async function doReview(action) {
    try {
      if (action === "approve") await api.post(`/reviews/${id}/approve`, { notes: reviewNotes });
      else if (action === "reject") await api.post(`/reviews/${id}/reject`, { notes: reviewNotes });
      else if (action === "command") await api.post(`/reviews/${id}/command`, { command: reviewCommand, notes: reviewNotes });
      toast.success("Action recorded");
      setReviewDlg(null); setReviewNotes(""); setReviewCommand("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (!audit) return <div className="text-slate-500">Loading audit...</div>;

  const totalQuestions = questions.length;
  const totalAnswered = (audit.answers || []).length;
  const progress = totalQuestions ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
  const displayState = getAuditDisplayState(audit);
  const isPreAudit = (audit.phase || "pre_audit") === "pre_audit";
  const isPreApproved = isPreAudit && audit.status === "approved";
  const isPostAudit = (audit.phase || "pre_audit") === "post_audit";
  const isPostActive = isPostAudit && (audit.status === "planned" || audit.status === "in_progress");

  async function startPostAudit() {
    try {
      await api.post(`/audits/${id}/advance-to-post-audit`);
      toast.success("Post-Audit started. You can now record post-audit answers.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back
          </button>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Audit Session</div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">{audit.audit_number}</h1>
            {hasUnsavedChanges && (
              <span
                data-testid="audit-unsaved-indicator"
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wider border border-amber-300"
                title="You have unsaved local answers. Click Save Draft to persist."
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                {Object.keys(pendingAnswers).length} unsaved
              </span>
            )}
            <ProgressBadge progress={displayState.progress} testId="audit-progress-badge" />
            <StageChip stage={displayState.stage} testId="audit-stage-badge" />
            {audit.grade && <GradeBadge grade={audit.grade} />}
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const targetDate = displayState.date.value;
              if (audit.status === "planned" && targetDate && targetDate > today) {
                const days = Math.round((new Date(targetDate) - new Date(today)) / 86400000);
                return (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-semibold bg-emerald-100 text-emerald-700 uppercase tracking-wider">
                    Ready to start · {days} day{days > 1 ? "s" : ""} ahead of H-day
                  </span>
                );
              }
              return null;
            })()}
          </div>
          {displayState.subText && (
            <div data-testid="audit-header-subtext" className="text-xs italic text-slate-500 mt-1">{displayState.subText}</div>
          )}
          <div className="text-sm text-slate-600 mt-1">
            {audit.supplier?.name} · {audit.plant?.name || "—"}
            {" · "}
            {displayState.date.key === "post_audit_date" && audit.post_audit_date ? (
              <><span className="text-indigo-700 font-semibold">Post-Audit:</span> {audit.post_audit_date}</>
            ) : (
              <><span className="text-sky-700 font-semibold">Pre-Audit:</span> {audit.pre_audit_date || audit.planned_date}</>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isPreApproved && (hasRole(user, "system_admin") || (hasRole(user, "auditor") && (audit.auditors || []).includes(user.id))) && (
            <Button
              data-testid="audit-start-post-btn"
              onClick={startPostAudit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <CtaIcon icon="start-post" className="mr-2" /> Start Post-Audit
            </Button>
          )}
          {canAnswer && (
            <Button
              data-testid="audit-save-draft-btn"
              onClick={saveDraft}
              disabled={savingDraft || Object.keys(pendingAnswers).length === 0}
              variant="outline"
              className="border-sams-primary text-sams-primary hover:bg-sams-primary/10"
            >
              <Save size={14} className="mr-2" />
              {savingDraft ? "Saving..." : (audit.status === "planned" ? "Save Draft (start audit)" : "Save Draft")}
            </Button>
          )}
          {canSubmit && (
            <Button data-testid={TID.audit.submitBtn} onClick={submitAudit} className="bg-sams-primary hover:bg-sams-primaryHover text-white">
              <Send size={14} className="mr-2" /> Submit for Review
            </Button>
          )}
          {canReview && (
            <>
              <Button data-testid={TID.review.approveBtn} onClick={() => setReviewDlg("approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 size={14} className="mr-2" /> Approve
              </Button>
              <Button data-testid={TID.review.rejectBtn} onClick={() => setReviewDlg("reject")} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                <XCircle size={14} className="mr-2" /> Reject
              </Button>
              <Button data-testid={TID.review.commandBtn} onClick={() => setReviewDlg("command")} variant="outline">
                <MessageSquare size={14} className="mr-2" /> Command
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Rejection notice — visible whenever the audit was returned to the auditor
          by the manager. Cleared on next submit or approve. */}
      {audit.rejection && (audit.status === "in_progress" || audit.status === "rejected") && (
        <div
          data-testid="rejection-banner"
          className="rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 flex items-start gap-3"
        >
          <XCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-red-900 text-sm">
              {audit.rejection.phase === "post_audit" ? "Post-Audit" : "Pre-Audit"} rejected by {audit.rejection.rejected_by_name || "Manager"}
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              Rejected on {(audit.rejection.rejected_at || "").slice(0, 16).replace("T", " ")}
            </div>
            {audit.rejection.notes && (
              <div className="mt-2 text-sm text-red-900 bg-white/60 border border-red-200 rounded px-3 py-2 whitespace-pre-wrap">
                {audit.rejection.notes}
              </div>
            )}
            <div className="text-xs text-red-800 mt-2 italic">Revise the answers below, then Save Draft and Submit for Review again.</div>
          </div>
        </div>
      )}

      {/* Post-Audit banner — visible when the auditor is working on post-audit phase */}
      {isPostActive && (
        <div
          data-testid="post-audit-banner"
          className="rounded-md border-l-4 border-emerald-500 bg-emerald-50 px-4 py-3 flex items-center gap-3"
        >
          <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
          <div>
            <div className="font-heading font-bold text-emerald-900 text-sm">Pre-Audit has been approved</div>
            <div className="text-xs text-emerald-800">You are now working on Post-Audit. Answers will be recorded against the Post-Audit phase.</div>
          </div>
        </div>
      )}

      {/* Score summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Score</div>
          <div className="mt-2 font-heading text-3xl font-black text-slate-900">{audit.percentage || 0}%</div>
          <div className="text-xs text-slate-500 mt-1">of {audit.max_score || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Progress</div>
          <div className="mt-2 font-heading text-3xl font-black text-slate-900">{progress}%</div>
          <div className="text-xs text-slate-500 mt-1">{totalAnswered} / {totalQuestions} answered</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Findings</div>
          <div className="mt-2 font-heading text-3xl font-black text-slate-900">{(audit.findings || []).length}</div>
          <div className="text-xs text-slate-500 mt-1">observations</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Attachments</div>
          <div className="mt-2 font-heading text-3xl font-black text-slate-900">{(audit.attachments || []).length}</div>
          <div className="text-xs text-slate-500 mt-1">files</div>
        </div>
      </div>

      {/* Main: category sidebar + questions */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Categories */}
        <div className="bg-white border border-slate-200 rounded-md p-3 h-fit sticky top-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 px-2 pb-2">Categories</div>
          <div className="space-y-1">
            {categories.map((c) => {
              const qs = questions.filter((q) => q.category_id === c.id);
              const ans = qs.filter((q) => answersByQ[q.id]).length;
              const active = activeCat === c.id;
              return (
                <button key={c.id} onClick={() => setActiveCat(c.id)}
                  data-testid={`cat-tab-${c.code}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-sams-primary text-white" : "hover:bg-slate-100 text-slate-700"}`}>
                  <div className="font-semibold truncate">{c.name}</div>
                  <div className={`text-[10px] mt-0.5 ${active ? "text-blue-100" : "text-slate-500"}`}>{ans}/{qs.length} answered</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Questions */}
        <div className="lg:col-span-3 space-y-3">
          {audit.status === "planned" && isAssigned && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-md bg-emerald-50 border border-emerald-200">
              <Info size={16} className="text-emerald-700 shrink-0" />
              <div className="flex-1 text-sm text-slate-800">
                {_phase === "post_audit"
                  ? "Pre-Audit has been approved. Complete Post-Audit answers and click Save Draft to continue."
                  : "Ready. Score the questions below and click Save Draft to start this audit."}
              </div>
            </div>
          )}
          {audit.status === "planned" && !isAssigned && !hasRole(user, "auditor") && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-md bg-blue-50 border border-blue-200">
              <Info size={16} className="text-sams-primary shrink-0" />
              <div className="flex-1 text-sm text-slate-800">
                This audit is view-only for your role. Only the assigned auditor can record answers.
              </div>
            </div>
          )}
          {canAnswer && remarkMissing.length > 0 && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-md bg-amber-50 border border-amber-200">
              <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-amber-900">
                <b>{remarkMissing.length}</b> question(s) with score 1 or 2 still need a remark.
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-slate-900">
              {categories.find((c) => c.id === activeCat)?.name || "Questions"}
            </h2>
            <div className="text-xs text-slate-500">{catAnswered} / {catQuestions.length} answered</div>
          </div>

          {catQuestions.map((q, idx) => {
            const savedA = answersByQ[q.id];
            const a = effectiveByQ[q.id]; // pending or saved
            const isPending = !!a?._pending;
            const atts = attByQ[q.id] || [];
            const currentScore = a?.score;
            const needsRemark = (currentScore === 1 || currentScore === 2) && !(a?.remark || "").trim();
            const scoreColors = {
              1: { active: "bg-red-600 text-white border-red-600 shadow-sm", idle: "bg-white text-red-700 border-red-200 hover:bg-red-50" },
              2: { active: "bg-orange-500 text-white border-orange-500 shadow-sm", idle: "bg-white text-orange-700 border-orange-200 hover:bg-orange-50" },
              3: { active: "bg-amber-500 text-white border-amber-500 shadow-sm", idle: "bg-white text-amber-700 border-amber-200 hover:bg-amber-50" },
              4: { active: "bg-sky-600 text-white border-sky-600 shadow-sm", idle: "bg-white text-sky-700 border-sky-200 hover:bg-sky-50" },
              5: { active: "bg-emerald-600 text-white border-emerald-600 shadow-sm", idle: "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50" },
            };
            const scoreLabels = { 1: "Poor", 2: "Fair", 3: "Good", 4: "Very Good", 5: "Excellent" };
            return (
              <div key={q.id} className={`bg-white border rounded-md p-4 transition-colors ${needsRemark ? "border-amber-300" : "border-slate-200"}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Q{idx + 1} · Weight {q.weight} · Max {q.max_score}
                  {savedA && !isPending && <span className="ml-2 text-emerald-700">✓ Answered</span>}
                  {isPending && <span className="ml-2 text-amber-700">● Unsaved</span>}
                </div>
                <div className="text-sm font-medium text-slate-900 mt-1">{q.question}</div>

                {/* Score buttons row */}
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Score</div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const isActive = currentScore === n;
                      const cls = isActive ? scoreColors[n].active : scoreColors[n].idle;
                      return (
                        <button
                          key={n}
                          type="button"
                          disabled={!canAnswer}
                          data-testid={`audit-score-btn-${q.id}-${n}`}
                          onClick={() => canAnswer && saveAnswer(q, n, a?.remark)}
                          className={`min-w-[64px] px-3 py-2 rounded-md border-2 font-heading font-black text-lg transition-all ${cls} disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          <div className="leading-none">{n}</div>
                          <div className={`text-[9px] font-semibold uppercase tracking-wider mt-1 ${isActive ? "text-white/90" : "text-slate-500"}`}>{scoreLabels[n]}</div>
                        </button>
                      );
                    })}
                    {a && canAnswer && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api.delete(`/audits/${id}/answers/${q.id}`);
                            load();
                          } catch (e) { /* endpoint may not exist; ignore */ }
                        }}
                        className="ml-auto text-xs text-slate-500 hover:text-red-600 px-2 self-center hidden"
                      >Clear</button>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label className={`text-[10px] uppercase tracking-wider font-semibold ${needsRemark ? "text-red-600" : "text-slate-500"}`}>
                      Remark / Notes {(currentScore === 1 || currentScore === 2) && <span className="text-red-600">* required</span>}
                    </Label>
                    <Input
                      value={a?.remark || ""}
                      onChange={(e) => canAnswer && a?.score && saveAnswer(q, a.score, e.target.value)}
                      onBlur={(e) => canAnswer && a?.score && a?.remark !== e.target.value && saveAnswer(q, a.score, e.target.value)}
                      disabled={!canAnswer || !a?.score}
                      data-testid={TID.audit.remarkInput(q.id)}
                      className={`mt-1 h-9 ${needsRemark ? "border-red-300 bg-red-50 focus:border-red-500" : ""}`}
                      placeholder={a?.score ? (needsRemark ? "⚠ Please explain the low score..." : "Add remark or observation...") : "Set a score first"} />
                    {needsRemark && (
                      <div className="text-[10px] text-red-600 font-semibold mt-1" data-testid={`remark-warning-${q.id}`}>
                        ⚠ Remark required for score {currentScore}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                      Photo evidence <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
                    </Label>
                    <button
                      disabled={!canAnswer}
                      onClick={() => { setUploadForQuestion(q.id); fileInputRef.current?.click(); }}
                      className="mt-1 w-full h-9 flex items-center justify-center gap-2 rounded-md border text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      <Camera size={14} /> Upload
                    </button>
                  </div>
                </div>

                {atts.length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap" data-testid={`evidence-thumbs-${q.id}`}>
                    {atts.map((att, i) => {
                      const isImage = /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(att.filename || att.url || "") ||
                                       (att.content_type || "").startsWith("image/");
                      const fullUrl = att.url?.startsWith("http") ? att.url : `${BACKEND}${att.url}`;
                      if (isImage) {
                        return (
                          <button
                            key={att.id}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openLightbox(atts, i); }}
                            data-testid={`evidence-thumb-${q.id}-${i}`}
                            className="group relative h-16 w-16 rounded-md overflow-hidden border border-slate-200 hover:border-sams-primary hover:shadow-sm transition-all"
                            title={att.filename}
                          >
                            <img
                              src={fullUrl}
                              alt={att.filename}
                              className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </button>
                        );
                      }
                      // Non-image attachment (PDF, etc) — keep as download link
                      return (
                        <a
                          key={att.id}
                          href={fullUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors flex items-center gap-1"
                        >
                          <FileText size={11} /> {att.filename}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {catQuestions.length === 0 && <div className="text-sm text-slate-500 italic p-6 text-center border border-dashed border-slate-200 rounded-md">No questions in this category</div>}
        </div>
      </div>

      {/* Findings */}
      <SectionCard
        title="Findings & Observations"
        subtitle="Non-conformities documented during this audit"
        actions={canAnswer && (
          <Button data-testid={TID.audit.findingAddBtn} onClick={() => setFindingDlg(true)} size="sm" className="bg-sams-primary hover:bg-sams-primaryHover text-white h-9">
            <Plus size={13} className="mr-1" /> Add Finding
          </Button>
        )}
      >
        {(audit.findings || []).length === 0 ? (
          <div className="text-sm text-slate-500 italic text-center py-6">No findings recorded.</div>
        ) : (
          <div className="space-y-2">
            {audit.findings.map((f) => (
              <div key={f.id} className="flex items-start gap-3 p-3 rounded-md border border-slate-200">
                <div className={`h-9 w-9 rounded-md grid place-items-center shrink-0 ${
                  f.severity === "Critical" ? "bg-red-100 text-red-700" :
                  f.severity === "High" ? "bg-orange-100 text-orange-700" :
                  f.severity === "Medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"
                }`}><AlertTriangle size={16} /></div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Severity: {f.severity}</div>
                  <div className="text-sm font-medium text-slate-900 mt-0.5">{f.description}</div>
                  {f.recommendation && <div className="text-xs text-slate-600 mt-1"><span className="font-semibold">Recommendation: </span>{f.recommendation}</div>}
                </div>
                {canAnswer && (
                  <Button size="sm" variant="ghost" onClick={() => deleteFinding(f.id)} className="text-red-600"><Trash2 size={13} /></Button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Manager reviews / commands history */}
      {(audit.reviews?.length > 0 || audit.commands?.length > 0) && (
        <SectionCard title="Review Activity" subtitle="Manager decisions and commands">
          <div className="space-y-2">
            {(audit.reviews || []).map((r) => (
              <div key={r.id} className="flex items-start gap-3 p-3 rounded-md border border-slate-200">
                <div className={`h-9 w-9 rounded-md grid place-items-center shrink-0 ${r.action === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {r.action === "approved" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{r.action} · {new Date(r.timestamp).toLocaleString()}</div>
                  {r.notes && <div className="text-sm text-slate-800 mt-0.5">{r.notes}</div>}
                </div>
              </div>
            ))}
            {(audit.commands || []).map((c) => (
              <div key={c.id} className="flex items-start gap-3 p-3 rounded-md border border-slate-200">
                <div className="h-9 w-9 rounded-md grid place-items-center shrink-0 bg-blue-100 text-blue-700"><MessageSquare size={16} /></div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Command · {new Date(c.timestamp).toLocaleString()}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-0.5">{c.command}</div>
                  {c.notes && <div className="text-xs text-slate-600 mt-1">{c.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Hidden upload input */}
      <input ref={fileInputRef} type="file" data-testid={TID.audit.uploadInput} accept="image/*,application/pdf" className="hidden" onChange={handleUpload} />

      {/* Finding dialog */}
      <Dialog open={findingDlg} onOpenChange={setFindingDlg}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Finding</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Severity</Label>
              <Select value={findingForm.severity} onValueChange={(v) => setFindingForm({ ...findingForm, severity: v })}>
                <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Description *</Label>
              <textarea value={findingForm.description} onChange={(e) => setFindingForm({ ...findingForm, description: e.target.value })}
                className="mt-1 w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Describe the non-conformity..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Recommendation</Label>
              <textarea value={findingForm.recommendation} onChange={(e) => setFindingForm({ ...findingForm, recommendation: e.target.value })}
                className="mt-1 w-full min-h-[60px] rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="What should the supplier do?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFindingDlg(false)}>Cancel</Button>
            <Button onClick={addFinding} className="bg-sams-primary hover:bg-sams-primaryHover text-white" data-testid="finding-save-button">Add finding</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialogs */}
      <Dialog open={!!reviewDlg} onOpenChange={(v) => !v && setReviewDlg(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {reviewDlg === "approve" ? "Approve Audit" : reviewDlg === "reject" ? "Reject Audit" : "Issue Command"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {reviewDlg === "command" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Command *</Label>
                <Input value={reviewCommand} onChange={(e) => setReviewCommand(e.target.value)} className="mt-1 h-10" placeholder="e.g., Re-check calibration records" data-testid="review-command-input" />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Notes</Label>
              <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
                className="mt-1 w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm"
                data-testid="review-notes-input" placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDlg(null)}>Cancel</Button>
            <Button onClick={() => doReview(reviewDlg)} data-testid="review-confirm-button"
              className={reviewDlg === "reject" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-sams-primary hover:bg-sams-primaryHover text-white"}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky bottom bar with Save Draft — visible when there are unsaved changes */}
      {canAnswer && hasUnsavedChanges && (
        <div
          data-testid="audit-sticky-save-bar"
          className="fixed left-0 right-0 bottom-0 z-30 border-t border-amber-300 bg-amber-50/95 backdrop-blur px-4 py-3 flex items-center justify-end gap-3 shadow-lg"
        >
          <div className="flex-1 flex items-center gap-2 text-sm text-amber-900">
            <AlertTriangle size={16} className="text-amber-600" />
            <span><b>{Object.keys(pendingAnswers).length}</b> unsaved answer{Object.keys(pendingAnswers).length > 1 ? "s" : ""}. Click <b>Save Draft</b> to persist{audit.status === "planned" ? " and start the audit." : "."}</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => { if (window.confirm("Discard all unsaved answers?")) setPendingAnswers({}); }}
            data-testid="audit-discard-draft-btn"
            className="text-slate-700"
          >Discard</Button>
          <Button
            onClick={saveDraft}
            disabled={savingDraft}
            data-testid="audit-save-draft-btn-sticky"
            className="bg-sams-primary hover:bg-sams-primaryHover text-white"
          >
            <Save size={14} className="mr-2" /> {savingDraft ? "Saving..." : "Save Draft"}
          </Button>
        </div>
      )}
      {/* Image lightbox for photo evidence */}
      {lightbox.open && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox({ open: false, images: [], index: 0 })}
          onIndexChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
        />
      )}
    </div>
  );
}
